/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchMemberListUrl,
  editMemberListUrl,
  fetchStatesUrl,
  fetchMemberUpdatedHistoryUrl,
} from 'src/services/urls'
import { getUniqueListBy } from 'src/lib/utils'

const initialState = {
  loading: false,
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  error: '',
  activePage: 0,
  states: [],
  updatedHistory: [],
}

// Fetch loyalty members list
export const fetchMemberList = createAsyncThunk(
  'memberList/fetchMemberList',
  async (options, { dispatch }) => {
    try {
      const skip = options?.skip
      const limit = options?.limit
      const mobileNumber = options?.mobileNumber
      const redeemedPoints = options?.redeemedPoints
      const states = options?.states
      const filter = options?.filter
      let docs = []
      let url = fetchMemberListUrl(skip, limit)
      if (redeemedPoints) {
        url = url + `&redeemedPoints=${redeemedPoints}`
      }
      if (mobileNumber) {
        url = url + `&mobileNumber=${mobileNumber}`
      }
      if (states && states.length > 0) {
        let s = states.map((s) => s.value)
        url = url + `&stateIds=${JSON.stringify(s)}`
      }
      const response = await makeRequest(url)
      if (response.data) docs = response.data.data.docs
      return { docs, data: response.data.data, filtered: mobileNumber || filter }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const fetchMemberUpdatedHistory = createAsyncThunk(
  'memberList/fetchMemberUpdatedHistory',
  async ({ growerId, setLoading }, { dispatch }) => {
    try {
      setLoading(true)
      const url = `${fetchMemberUpdatedHistoryUrl()}?userId=${growerId}`
      const response = await makeRequest(url, 'GET')
      setLoading(false)
      return response.data.data || []
    } catch (e) {
      setLoading(false)
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

// Edit/Update or enable/disable a loyalty member
export const editMemberList = createAsyncThunk(
  'memberList/editMemberList',
  async (data, { dispatch }) => {
    try {
      let body = {
        growerId: data?.growerId,
        status: data?.status,
        updatedBy: data?.updatedBy,
        comments: data?.comments,
      }

      const response = await makeRequest(editMemberListUrl(), 'PUT', body)

      const { displayMessage, status } = response.data
      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
      }
      const fetchFilters = {
        limit: 500,
        filter: true,
        redeemedPoints: data?.filterRedeemedPoints || 0,
        mobileNumber: data?.filterMobileNumber || '',
        states: data?.filterStates || [],
      }
      dispatch(fetchMemberList(fetchFilters))
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const fetchAllStates = createAsyncThunk(
  'memberList/fetchStates',
  async (_, { dispatch }) => {
    try {
      const response = await makeRequest(fetchStatesUrl(), 'GET')
      return response.data.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

const memberListSlice = createSlice({
  name: 'memberList',
  initialState,
  reducers: {
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    resetMemberList: (state) => {
      state.data = { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 }
      state.docs = []
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setUpdatedHistory: (state, action) => {
      state.updatedHistory = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMemberList.pending, (state) => {
      state.loading = state.docs.length > 0 ? false : true
    })
    builder.addCase(fetchMemberList.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (action.payload.docs) {
        state.data = action.payload.data
        const oldData = action.payload.filtered ? [] : state.docs
        const totalData = [...oldData, ...action.payload.docs]
        const uniqueData = getUniqueListBy(totalData, 'displayId')
        state.docs = uniqueData
      }
    })
    builder.addCase(fetchMemberList.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(editMemberList.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editMemberList.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
    })
    builder.addCase(fetchAllStates.fulfilled, (state, action) => {
      state.states = action.payload.reduce((acc, curr) => {
        if (curr.state !== 'All') {
          return [...acc, { label: curr.state, value: curr._id }]
        }
        return acc
      }, [])
      state.loading = false
      state.error = ''
    })
    builder.addCase(fetchAllStates.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
      state.updatedHistory = []
    })
    builder.addCase(fetchMemberUpdatedHistory.fulfilled, (state, action) => {
      state.error = ''
      state.updatedHistory = action.payload
    })
    builder.addCase(fetchMemberUpdatedHistory.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message
      state.updatedHistory = []
    })
  },
})

export default memberListSlice.reducer
export const { setActivePage, resetMemberList, setLoading, setUpdatedHistory } =
  memberListSlice.actions
